import clsx from 'clsx';
import { AlarmClock, ExternalLink } from 'lucide-react';
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, diffDays, isoWeek, progressOf, type AnalysisTask, type GanttItem, type GroupStat, type TimelinePoint } from '@pz/core';
import { d, h, month, num } from '../../lib/format';
import { DataTable, Frame } from '../charts';
import { FormaIssueLink } from '../entry';
import { useTip } from '../ui';
import { hoursFill, hoursInk, pctOr, progFill, progInk, StageBar, StageLegend, STAGE_FILL } from './common';

/* ------------------------------------------------------------------ */
/* Progress per group                                                   */
/* ------------------------------------------------------------------ */

/** Progress of each group (trade, floor, ...) as a bar of the four stages, with share, counts, overdue and hours. */
export function GroupProgress({
  groups,
  progressBy,
  head,
  valueLabel,
  onSelect,
  showHours = true,
}: {
  groups: GroupStat[];
  progressBy: 'count' | 'plan';
  head: string;
  valueLabel: (v: string) => string;
  onSelect?: (key: string) => void;
  showHours?: boolean;
}) {
  const { t } = useTranslation();
  const tip = useTip();
  if (!groups.length) return <p className="text-sm text-graphite">{t('analysis.noData')}</p>;
  return (
    <Frame
      legend={<StageLegend />}
      table={
        <DataTable
          head={head}
          columns={[
            { key: 'tasks', label: t('analysis.kpi.tasks') },
            { key: 'done', label: t('analysis.stages.done') },
            { key: 'progress', label: t('analysis.kpi.progress') },
            { key: 'overdue', label: t('analysis.kpi.overdue') },
            ...(showHours ? [{ key: 'hours', label: t('analysis.kpi.actual') }] : []),
          ]}
          rows={groups.map((g) => ({
            label: valueLabel(g.key),
            values: { tasks: num(g.tasks), done: num(g.done), progress: pctOr(progressOf(g, progressBy)), overdue: num(g.overdue), hours: `${h(g.actualMinutes, 1)} h` },
          }))}
        />
      }
    >
      <ul className="flex flex-col gap-0.5">
        {groups.map((g) => {
          const p = progressOf(g, progressBy);
          const content = (
            <>
              <span className="truncate" title={valueLabel(g.key)}>
                {valueLabel(g.key)}
              </span>
              <StageBar s={g} />
              <span className="num text-right font-semibold">{pctOr(p)}</span>
              <span className="num text-right text-xs text-graphite">
                {num(g.done)}/{num(g.tasks)}
              </span>
              <span className="num flex items-center justify-end gap-1 text-xs">
                {g.overdue > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-bad" title={t('analysis.overdueCount', { count: g.overdue })}>
                    <AlarmClock className="size-3.5" aria-hidden />
                    {g.overdue}
                  </span>
                )}
              </span>
              {showHours && <span className="num text-right text-xs text-graphite">{h(g.actualMinutes, 0)} h</span>}
            </>
          );
          const cls = clsx(
            'grid w-full items-center gap-2 rounded-sm px-1 py-1 text-left text-sm',
            showHours ? 'grid-cols-[minmax(6rem,13rem)_minmax(0,1fr)_3.2rem_3.6rem_2.6rem_3.6rem]' : 'grid-cols-[minmax(6rem,13rem)_minmax(0,1fr)_3.2rem_3.6rem_2.6rem]',
          );
          const details = (
            <div className="flex flex-col gap-0.5">
              <div className="font-semibold">{valueLabel(g.key)}</div>
              <div className="num">{t('analysis.tipProgress', { progress: pctOr(p), done: num(g.done), tasks: num(g.tasks) })}</div>
              <div className="num text-graphite">{t('analysis.tipStages', { progress: g.progress, review: g.review, open: g.open })}</div>
              {(g.overdue > 0 || g.dueSoon > 0) && <div className="num text-bad">{t('analysis.tipDue', { overdue: g.overdue, soon: g.dueSoon })}</div>}
              <div className="num text-graphite">{t('analysis.tipHours', { actual: h(g.actualMinutes, 1), plan: h(g.plannedMinutes, 0) })}</div>
            </div>
          );
          return (
            <li key={g.key}>
              {onSelect ? (
                <button type="button" className={clsx(cls, 'hover:bg-sheet-2 focus-visible:bg-sheet-2')} onClick={() => onSelect(g.key)} {...tip.bind(details)}>
                  {content}
                </button>
              ) : (
                <div tabIndex={0} className={cls} {...tip.bind(details)}>
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {tip.node}
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* Burn-up                                                              */
/* ------------------------------------------------------------------ */

/**
 * Burn-up: tasks in the project, tasks due by their due date and finished tasks at the end of each week. The gap
 * between "due" and "finished" is the backlog against the schedule.
 */
export function BurnUp({ points, tasks, height = 220 }: { points: TimelinePoint[]; tasks: AnalysisTask[]; height?: number }) {
  const { t } = useTranslation();
  const [active, setActive] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const due = useMemo(() => {
    const dues = tasks.filter((x) => !x.archived && x.dueDate).map((x) => x.dueDate!).sort();
    return points.map((p) => {
      const end = addDays(p.week, 6);
      let n = 0;
      for (const dd of dues) if (dd <= end) n++;
      return n;
    });
  }, [points, tasks]);
  if (points.length < 2) return <p className="text-sm text-graphite">{t('analysis.noData')}</p>;
  const W = 640;
  const H = height;
  const L = 34;
  const R = 8;
  const T = 10;
  const B = 24;
  const max = Math.max(1, ...points.map((p) => p.total), ...due);
  const top = Math.ceil(max / 10) * 10;
  const x = (i: number) => L + (i / (points.length - 1)) * (W - L - R);
  const y = (v: number) => T + (1 - v / top) * (H - T - B);
  const line = (vals: number[]) => vals.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const finished = points.map((p) => p.finished);
  const area = `${line(finished)} L${x(points.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(top * f));
  const every = Math.max(1, Math.ceil(points.length / 10));
  const onMove = (e: ReactPointerEvent) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const px = ((e.clientX - r.left) / r.width) * W;
    const i = Math.round(((px - L) / (W - L - R)) * (points.length - 1));
    setActive(Math.max(0, Math.min(points.length - 1, i)));
  };
  const a = active !== null ? points[active] : null;
  return (
    <Frame
      legend={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-4 rounded bg-ink" /> {t('analysis.burn.total')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-0 w-4 border-t-2 border-dashed border-viz-mid" /> {t('analysis.burn.due')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-4 rounded-[3px] bg-viz-strong" /> {t('analysis.burn.finished')}
          </span>
        </div>
      }
      table={
        <DataTable
          head={t('common.week')}
          columns={[
            { key: 'total', label: t('analysis.burn.total') },
            { key: 'due', label: t('analysis.burn.due') },
            { key: 'finished', label: t('analysis.burn.finished') },
          ]}
          rows={points.map((p, i) => ({ label: `${t('kpi.weekPrefix')} ${isoWeek(p.week).week} (${d(p.week)})`, values: { total: num(p.total), due: num(due[i]), finished: num(p.finished) } }))}
        />
      }
    >
      <div ref={ref} className="relative" onPointerMove={onMove} onPointerLeave={() => setActive(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} className="block w-full" role="img" aria-label={t('analysis.burn.aria', { finished: points[points.length - 1].finished, total: points[points.length - 1].total })}>
          {ticks.map((v) => (
            <g key={v}>
              <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={v === 0 ? 'var(--line-strong)' : 'var(--line)'} strokeWidth={1} />
              <text x={L - 6} y={y(v) + 3.5} fontSize={10.5} textAnchor="end" fill="var(--graphite)" className="num">
                {v}
              </text>
            </g>
          ))}
          {points.map((p, i) =>
            i % every === 0 || i === points.length - 1 ? (
              <text key={p.week} x={x(i)} y={H - 6} fontSize={10.5} textAnchor="middle" fill="var(--graphite)" className="num">
                {isoWeek(p.week).week}
              </text>
            ) : null,
          )}
          <path d={area} fill="var(--viz-soft)" opacity={0.45} />
          <path d={line(finished)} fill="none" stroke="var(--viz-strong)" strokeWidth={2} strokeLinejoin="round" />
          <path d={line(due)} fill="none" stroke="var(--viz-mid)" strokeWidth={2} strokeDasharray="5 4" strokeLinejoin="round" />
          <path d={line(points.map((p) => p.total))} fill="none" stroke="var(--ink)" strokeWidth={1.5} strokeLinejoin="round" />
          {active !== null && (
            <g>
              <line x1={x(active)} x2={x(active)} y1={T} y2={H - B} stroke="var(--graphite)" strokeWidth={1} strokeDasharray="2 3" />
              <circle cx={x(active)} cy={y(finished[active])} r={4} fill="var(--viz-strong)" stroke="var(--sheet)" strokeWidth={2} />
              <circle cx={x(active)} cy={y(due[active])} r={3.5} fill="var(--viz-mid)" stroke="var(--sheet)" strokeWidth={2} />
              <circle cx={x(active)} cy={y(points[active].total)} r={3.5} fill="var(--ink)" stroke="var(--sheet)" strokeWidth={2} />
            </g>
          )}
        </svg>
        {a && active !== null && (
          <div
            className="pointer-events-none absolute top-0 z-10 flex w-max flex-col gap-0.5 rounded-md border border-line bg-sheet px-2.5 py-1.5 text-xs shadow-md"
            style={{ left: `${(x(active) / W) * 100}%`, transform: active > points.length / 2 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)' }}
          >
            <div className="font-medium text-graphite">
              {t('kpi.weekPrefix')} {isoWeek(a.week).week} ({d(a.week)})
            </div>
            <div className="num">
              <strong>{num(a.total)}</strong> {t('analysis.burn.total')}
            </div>
            <div className="num">
              <strong>{num(due[active])}</strong> {t('analysis.burn.due')}
            </div>
            <div className="num">
              <strong>{num(a.finished)}</strong> {t('analysis.burn.finished')}
            </div>
            <div className="num text-graphite">{t('analysis.burn.week', { done: a.done, created: a.created, hours: h(a.minutes, 0) })}</div>
          </div>
        )}
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* Gantt                                                                */
/* ------------------------------------------------------------------ */

/** Tasks as bars between start and due date, grouped; today line; overdue bars outlined in red. */
export function Gantt({
  items,
  today,
  groupLabel,
  formaLink,
  limit,
}: {
  items: GanttItem[];
  today: string;
  groupLabel: (v: string) => string;
  formaLink?: (id: string) => string | null;
  limit: number;
}) {
  const { t } = useTranslation();
  const tip = useTip();
  if (!items.length) return <p className="text-sm text-graphite">{t('analysis.gantt.empty')}</p>;
  let from = items.reduce((m, i) => (i.start < m ? i.start : m), items[0].start);
  let to = items.reduce((m, i) => (i.end > m ? i.end : m), items[0].end);
  if (today < from) from = today;
  if (today > to) to = today;
  from = addDays(from, -3);
  to = addDays(to, 3);
  const days = Math.max(14, diffDays(to, from));
  const pos = (date: string) => `${(Math.max(0, Math.min(days, diffDays(date, from))) / days) * 100}%`;
  const months: { at: string; label: string }[] = [];
  for (let m = `${from.slice(0, 7)}-01`; m <= to; m = addDays(m, 32).slice(0, 7) + '-01') if (m >= from) months.push({ at: m, label: month(m) });
  const rows: ({ kind: 'group'; label: string; count: number } | { kind: 'item'; item: GanttItem })[] = [];
  let last: string | null = null;
  for (const i of items) {
    if (i.group !== last) {
      rows.push({ kind: 'group', label: groupLabel(i.group), count: items.filter((x) => x.group === i.group).length });
      last = i.group;
    }
    rows.push({ kind: 'item', item: i });
  }
  const widthPx = Math.max(640, Math.min(2400, days * 6));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-graphite">
        <StageLegend />
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-4 rounded-[3px] border-2 border-bad" /> {t('analysis.gantt.overdue')}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-0.5 bg-bad" /> {t('common.today')}
          </span>
        </span>
      </div>
      <div className="scroll-x max-h-[70vh] overflow-y-auto rounded-md border border-line">
        <div className="grid min-w-full" style={{ gridTemplateColumns: `minmax(14rem, 20rem) ${widthPx}px` }}>
          <div className="sticky top-0 left-0 z-20 border-b border-line bg-sheet-2 px-2 py-1 text-xs font-medium text-graphite">{t('common.task')}</div>
          <div className="sticky top-0 z-10 border-b border-line bg-sheet-2">
            <div className="relative h-6">
              {months.map((m) => (
                <span key={m.at} className="absolute top-0 h-full border-l border-line pl-1 text-[11px] leading-6 text-graphite" style={{ left: pos(m.at) }}>
                  {m.label}
                </span>
              ))}
            </div>
          </div>
          {rows.map((r, n) =>
            r.kind === 'group' ? (
              <div key={`g${n}`} className="contents">
                <div className="sticky left-0 z-10 border-b border-line bg-sheet-2 px-2 py-1 text-xs font-semibold">
                  {r.label} <span className="font-normal text-graphite">({r.count})</span>
                </div>
                <div className="border-b border-line bg-sheet-2" />
              </div>
            ) : (
              <div key={r.item.id} className="contents">
                <div className="sticky left-0 z-10 flex min-w-0 items-center gap-1.5 border-b border-line/60 bg-sheet px-2 py-0.5 text-xs">
                  <span className="num shrink-0 font-semibold text-accent">{r.item.ref}</span>
                  <span className="truncate" title={r.item.title}>
                    {r.item.title}
                  </span>
                  {formaLink?.(r.item.id) && (
                    <FormaIssueLink href={formaLink(r.item.id)!} className="ml-auto shrink-0 text-graphite hover:text-accent" title={t('task.openInForma')}>
                      <ExternalLink className="size-3" />
                    </FormaIssueLink>
                  )}
                </div>
                <div className="relative border-b border-line/60">
                  {months.map((m) => (
                    <span key={m.at} className="absolute inset-y-0 border-l border-line/50" style={{ left: pos(m.at) }} aria-hidden />
                  ))}
                  <span className="absolute inset-y-0 w-0.5 bg-bad/70" style={{ left: pos(today) }} aria-hidden />
                  <span
                    tabIndex={0}
                    role="img"
                    aria-label={`${r.item.ref} ${r.item.title}: ${d(r.item.start)} - ${d(r.item.end)}, ${t(`analysis.stages.${r.item.stage}`)}${r.item.overdue ? `, ${t('analysis.gantt.overdue')}` : ''}`}
                    {...tip.bind(
                      <div className="flex flex-col gap-0.5">
                        <div className="font-semibold">
                          {r.item.ref} {r.item.title}
                        </div>
                        <div className="num">
                          {d(r.item.start)} - {d(r.item.end)}
                          {!r.item.hasDue && ` (${t('analysis.gantt.noDue')})`}
                        </div>
                        <div>{t(`analysis.stages.${r.item.stage}`)}</div>
                        {r.item.overdue && <div className="font-medium text-bad">{t('analysis.gantt.overdueDays', { count: diffDays(today, r.item.end) })}</div>}
                      </div>,
                    )}
                    className={clsx('absolute top-1 bottom-1 min-w-1.5 rounded-[3px] outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]', r.item.stage === 'open' && 'border border-line-strong', r.item.overdue && 'border-2 border-bad')}
                    style={{ left: pos(r.item.start), width: `calc(${pos(addDays(r.item.end, 1))} - ${pos(r.item.start)})`, background: STAGE_FILL[r.item.stage] }}
                  />
                </div>
              </div>
            ),
          )}
        </div>
      </div>
      {items.length >= limit && <p className="text-xs text-graphite">{t('analysis.gantt.limited', { count: limit })}</p>}
      {tip.node}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Who works where                                                      */
/* ------------------------------------------------------------------ */

/** Hours per person and value of a dimension in the period; cells shaded by hours (one ramp), totals. */
export function PersonHours({
  pm,
  colLabel,
  valueLabel,
  personName,
}: {
  pm: { cols: { key: string; minutes: number }[]; rows: { personId: string; total: number; cells: number[] }[] };
  colLabel: string;
  valueLabel: (v: string) => string;
  personName: (id: string) => string;
}) {
  const { t } = useTranslation();
  if (!pm.rows.length) return <p className="text-sm text-graphite">{t('analysis.noHours')}</p>;
  const max = Math.max(1, ...pm.rows.flatMap((r) => r.cells));
  return (
    <div className="scroll-x">
      <table className="border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-sheet px-2 py-1.5 text-left text-xs font-medium text-graphite">
              {t('analysis.dims.person')} × {colLabel}
            </th>
            {pm.cols.map((c) => (
              <th key={c.key} className="max-w-32 px-1 py-1.5 text-center align-bottom text-xs font-medium text-graphite">
                <span className="line-clamp-3 hyphens-auto [overflow-wrap:anywhere]" title={valueLabel(c.key)}>
                  {valueLabel(c.key)}
                </span>
              </th>
            ))}
            <th className="px-2 py-1.5 text-right text-xs font-semibold text-graphite">{t('common.total')}</th>
          </tr>
        </thead>
        <tbody>
          {pm.rows.map((r) => (
            <tr key={r.personId}>
              <th className="sticky left-0 z-10 bg-sheet px-2 py-0.5 text-left text-xs font-medium whitespace-nowrap">{personName(r.personId)}</th>
              {r.cells.map((m, i) => (
                <td key={pm.cols[i].key} className="p-0.5">
                  {m > 0 ? (
                    <span
                      className="num flex h-8 min-w-14 items-center justify-center rounded text-xs font-medium"
                      style={{ background: hoursFill(m / max), color: hoursInk(m / max) }}
                      title={`${personName(r.personId)} · ${valueLabel(pm.cols[i].key)}: ${h(m, 1)} h`}
                    >
                      {h(m, 0)}
                    </span>
                  ) : (
                    <span className="block h-8 min-w-14 rounded border border-dashed border-line" />
                  )}
                </td>
              ))}
              <td className="num px-2 text-right text-xs font-semibold">{h(r.total, 0)} h</td>
            </tr>
          ))}
          <tr>
            <th className="sticky left-0 z-10 bg-sheet px-2 py-1 text-left text-xs font-semibold text-graphite">{t('common.total')}</th>
            {pm.cols.map((c) => (
              <td key={c.key} className="num px-1 py-1 text-center text-xs font-semibold">
                {h(c.minutes, 0)}
              </td>
            ))}
            <td className="num px-2 text-right text-xs font-semibold">{h(pm.rows.reduce((s, r) => s + r.total, 0), 0)} h</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-1 text-xs text-graphite">{t('analysis.hoursUnit')}</p>
    </div>
  );
}

/** Open tasks past their due date, longest first. */
export function OverdueList({ items, today, extra }: { items: { task: AnalysisTask; days: number }[]; today: string; extra?: (t: AnalysisTask) => ReactNode }) {
  const { t } = useTranslation();
  if (!items.length) return <p className="text-sm text-graphite">{t('analysis.noOverdue')}</p>;
  return (
    <ul className="flex flex-col divide-y divide-line/70">
      {items.map(({ task, days }) => (
        <li key={task.id} className="flex items-start gap-2 py-1.5 text-sm">
          <AlarmClock className="mt-0.5 size-4 shrink-0 text-bad" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="num font-semibold text-accent">{task.ref}</span> {task.title}
            <span className="block text-xs text-graphite">
              {t('analysis.dueOn', { date: d(task.dueDate) })} · {t(`task.status.${task.status as 'open'}`, { defaultValue: task.status })}
              {extra?.(task)}
            </span>
          </span>
          <span className="num shrink-0 text-xs font-semibold text-bad" title={today}>
            {t('analysis.daysLate', { count: days })}
          </span>
        </li>
      ))}
    </ul>
  );
}
